import { Button, type ButtonProps } from "@/components/flexkit/Button";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { ExternalLink } from "@/components/podkit/typography/ExternalLink";
import { useSegmentTrack } from "@/hooks/use-segment";
import type { WithTrackingId } from "@/types/tracking";
import { useCallback, useState, type FC } from "react";

export const ProviderOption: FC<
    {
        name: string;
        Logo: FC<PropsWithClassName>;
        label: string;
        onClick?: () => void;
        href?: string;
        variant?: ButtonProps["variant"];
    } & WithTrackingId
> = ({ name, Logo, label, onClick, href, variant = "secondary", "data-tracking-id": trackingId }) => {
    const isDisabled = !onClick && !href;

    const segmentTrack = useSegmentTrack();
    const [waitListJoined, setWaitListJoined] = useState(false);

    const onJoinWaitlist = useCallback(() => {
        segmentTrack("waitlist_joined", {
            feature: `Runner - ${name}`,
            provider: name,
            type: "infrastructure",
        });
        setWaitListJoined(true);
    }, [segmentTrack, setWaitListJoined, name]);
    const waitlistLabel = waitListJoined ? "Joined waitlist" : "Join waitlist";

    return (
        <div className="flex items-center justify-between rounded-xl border border-border-light p-2">
            <div className="flex items-center">
                <Logo className={cn("mr-3 size-8", isDisabled && "grayscale")} />
                <div className="text-base font-medium text-content-secondary">{name}</div>
            </div>
            {href ? (
                <Button
                    variant={variant}
                    onClick={onClick}
                    aria-label={`${label} Provider: ${name}`}
                    data-tracking-id={trackingId}
                    asChild
                >
                    <ExternalLink href={href} className="text-base font-medium text-content-primary">
                        {label}
                    </ExternalLink>
                </Button>
            ) : onClick ? (
                <Button
                    variant={variant}
                    onClick={onClick}
                    aria-label={`${label} Provider: ${name}`}
                    data-tracking-id={trackingId}
                >
                    {label}
                </Button>
            ) : (
                <Button
                    variant="link"
                    size="md"
                    className="disabled:bg-transparent disabled:text-content-success"
                    aria-label={`${label}: ${name}`}
                    disabled={waitListJoined}
                    onClick={onJoinWaitlist}
                    data-tracking-id={trackingId}
                >
                    {waitlistLabel}
                </Button>
            )}
        </div>
    );
};
